import { Dispatch } from "redux";
import { thinkApi } from "../../Api/Microservices";
import { Chat, ChatApiResponse, Payload } from "../../Models/Chat";
import {
  FeedBackRequest,
  IsConversationUpdatedRequest,
  StarRequest,
} from "../../Models/Request/SASUrlRequest";
import { buildAppError } from "../../Utils/AppError";
import {
  ADD_CONVERSATION_FAILURE,
  ADD_CONVERSATION_REQUEST,
  ADD_CONVERSATION_SUCCESS,
  FETCH_CONVERSATION_BY_ID_FAILURE,
  FETCH_CONVERSATION_BY_ID_REQUEST,
  FETCH_CONVERSATION_BY_ID_SUCCESS,
  HIDE_CONVERSATION_PANE,
  IS_CONVERSATION_UPDATED,
  REGENERATE_CHAT_FAILURE,
  REGENERATE_CHAT_REQUEST,
  REGENERATE_CHAT_SUCCESS,
  SET_DEFAULT_CONVERSATION,
  SHOW_CONVERSATION_PANE,
  UPDATE_CHAT_FAILURE,
  UPDATE_CHAT_REQUEST,
  UPDATE_CHAT_SUCCESS,
  UPDATE_FEEDBACK_FAILURE,
  UPDATE_FEEDBACK_REQUEST,
  UPDATE_FEEDBACK_SUCCESS,
  UPDATE_STAR_FAILURE,
  UPDATE_STAR_REQUEST,
  UPDATE_STAR_SUCCESS,
} from "./ChatConversationType";

const setAuthorizationHeader = (token: any) => {
  thinkApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

export const fetchConversationByIdRequest = () => ({
  type: FETCH_CONVERSATION_BY_ID_REQUEST,
});

export const fetchConversationByIdSuccess = (
  conversation: ChatApiResponse
) => ({
  type: FETCH_CONVERSATION_BY_ID_SUCCESS,
  payload: conversation,
});

export const fetchConversationByIdFailure = (error: any) => ({
  type: FETCH_CONVERSATION_BY_ID_FAILURE,
  payload: error,
});

export const addConversationRequest = () => ({
  type: ADD_CONVERSATION_REQUEST,
});

export const addConversationSuccess = (conversation: ChatApiResponse) => ({
  type: ADD_CONVERSATION_SUCCESS,
  payload: conversation,
});

export const addConversationFailure = (error: any) => ({
  type: ADD_CONVERSATION_FAILURE,
  payload: error,
});

export const updateChatRequest = () => ({
  type: UPDATE_CHAT_REQUEST,
});

export const updateChatSuccess = (chat: Chat) => ({
  type: UPDATE_CHAT_SUCCESS,
  payload: chat,
});

export const updateChatFailure = (error: any) => ({
  type: UPDATE_CHAT_FAILURE,
  payload: error,
});

export const updateStarRequest = () => ({
  type: UPDATE_STAR_REQUEST,
});

export const updateStarSuccess = (value: any) => ({
  type: UPDATE_STAR_SUCCESS,
  payload: value,
});

export const updateStarFailure = (error: any) => ({
  type: UPDATE_STAR_FAILURE,
  payload: error,
});

export const updateFeedbackRequest = () => ({
  type: UPDATE_FEEDBACK_REQUEST,
});

export const updateFeedbackSuccess = (value: any) => ({
  type: UPDATE_FEEDBACK_SUCCESS,
  payload: value,
});

export const updateFeedbackFailure = (error: any) => ({
  type: UPDATE_FEEDBACK_FAILURE,
  payload: error,
});

export const regenerateChatRequest = (chatId: string) => ({
  type: REGENERATE_CHAT_REQUEST,
  payload: { id: chatId },
});

export const regenerateChatSuccess = (value: any) => ({
  type: REGENERATE_CHAT_SUCCESS,
  payload: value,
});

export const regenerateChatFailure = (error: any) => ({
  type: REGENERATE_CHAT_FAILURE,
  payload: error,
});

export const hideConversationPane = () => ({
  type: HIDE_CONVERSATION_PANE,
});

export const showConversationPane = () => ({
  type: SHOW_CONVERSATION_PANE,
});

export const setDefaultConversation = () => ({
  type: SET_DEFAULT_CONVERSATION,
});

export const isConversationUpdatedSuccess = (value: boolean) => ({
  type: IS_CONVERSATION_UPDATED,
  payload: value,
});

export const fetchConversationById = (
  token: string | null,
  conversationId: string | undefined
) => {
  return async (dispatch: Dispatch) => {
    if (token) {
      dispatch(fetchConversationByIdRequest());
      try {
        setAuthorizationHeader(token);
        const response = await thinkApi.post<ChatApiResponse>(
          `/Conversation/${conversationId}`,
          {}
        );

        dispatch(fetchConversationByIdSuccess(response.data));
      } catch (error: any) {
        const appError = buildAppError(error, "");
        dispatch(fetchConversationByIdFailure(appError.message));
      }
    }
  };
};

export const addConversation = (value: ChatApiResponse) => {
  return async (dispatch: Dispatch) => {
    dispatch(addConversationRequest());
    try {
      dispatch(addConversationSuccess(value));
    } catch (error: any) {
      const appError = buildAppError(error, "");
      dispatch(addConversationFailure(appError.message));
    }
  };
};

export const updateConversation = (value: Chat) => {
  return async (dispatch: Dispatch) => {
    dispatch(updateChatRequest());
    try {
      dispatch(updateChatSuccess(value));
    } catch (error: any) {
      const appError = buildAppError(error, "");
      dispatch(updateChatFailure(appError.message));
    }
  };
};

export const regenerateConversation = (
  token: string | null,
  value: Payload,
  conversationId: string | undefined,
  chatId: string
) => {
  return async (dispatch: Dispatch) => {
    dispatch(regenerateChatRequest(chatId));
    try {
      setAuthorizationHeader(token);
      const response = await thinkApi.put<Chat>(
        `/conversation/${conversationId}/chat/${chatId}`,
        value
      );
      dispatch(regenerateChatSuccess(response.data));
    } catch (error: any) {
      const appError = buildAppError(error, "");
      dispatch(regenerateChatFailure(appError.message));
    }
  };
};

export const updateStarDetails = (token: string | null, value: StarRequest) => {
  return async (dispatch: Dispatch) => {
    dispatch(updateStarRequest());
    try {
      setAuthorizationHeader(token);
      await thinkApi.put(`/conversation/star`, value);
      dispatch(updateStarSuccess(value));
    } catch (error: any) {
      const appError = buildAppError(error, "");
      dispatch(updateStarFailure(appError.message));
    }
  };
};

export const updateFeedbackDetails = (
  token: string | null,
  value: FeedBackRequest
) => {
  return async (dispatch: Dispatch) => {
    dispatch(updateFeedbackRequest());
    try {
      setAuthorizationHeader(token);
      await thinkApi.put(`/conversation/rating`, value);
      dispatch(updateFeedbackSuccess(value));
    } catch (error: any) {
      const appError = buildAppError(error, "");
      dispatch(updateFeedbackFailure(appError.message));
    }
  };
};

export const updateFeedbackTagDetails = (
  token: string | null,
  value: FeedBackRequest
) => {
  return async (dispatch: Dispatch) => {
    try {
      setAuthorizationHeader(token);
      await thinkApi.put(`/conversation/feedback`, value);
    } catch (error: any) {
      const appError = buildAppError(error, "");
    }
  };
};

export const conversationUpdates = (
  token: string | null,
  conversationId: string | undefined,
  value: IsConversationUpdatedRequest
) => {
  return async (dispatch: Dispatch) => {
    try {
      setAuthorizationHeader(token);
      const response = await thinkApi.post<boolean>(
        `/conversation/${conversationId}/updates`,
        value
      );
      dispatch(isConversationUpdatedSuccess(response.data));
    } catch (error: any) {
      const appError = buildAppError(error, "");
    }
  };
};
